import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthRedirectService {
  private static readonly STORAGE_KEY = 'fundcube_return_url';
  private readonly router = inject(Router);

  /**
   * Valida se a URL de retorno é um caminho interno seguro.
   */
  isValidReturnUrl(url: string | null | undefined): boolean {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return false;
    if (
      trimmed.startsWith('/auth/login') ||
      trimmed.startsWith('/auth/register')
    ) {
      return false;
    }
    return true;
  }

  /**
   * Guarda a URL de destino no armazenamento local.
   */
  setReturnUrl(url: string | null | undefined): void {
    if (!url || !this.isValidReturnUrl(url)) return;
    try {
      localStorage.setItem(AuthRedirectService.STORAGE_KEY, url.trim());
    } catch {}
  }

  /**
   * Obtém a URL de destino (prioriza queryParam da rota ativa, senão consulta o storage).
   */
  getReturnUrl(route?: ActivatedRoute): string | null {
    if (route) {
      const qp =
        route.snapshot.queryParamMap.get('returnUrl') ||
        route.snapshot.queryParams?.['returnUrl'];
      if (qp && this.isValidReturnUrl(qp)) {
        this.setReturnUrl(qp);
        return qp;
      }
    }

    try {
      const stored = localStorage.getItem(AuthRedirectService.STORAGE_KEY);
      if (stored && this.isValidReturnUrl(stored)) {
        return stored;
      }
    } catch {}

    return null;
  }

  /**
   * Remove a URL de destino guardada após utilização.
   */
  clearReturnUrl(): void {
    try {
      localStorage.removeItem(AuthRedirectService.STORAGE_KEY);
    } catch {}
  }

  /**
   * Redireciona para o registo guardando a URL de destino.
   */
  redirectToRegister(returnUrl?: string): void {
    const target = returnUrl || this.router.url;
    if (this.isValidReturnUrl(target)) {
      this.setReturnUrl(target);
      this.router.navigate(['/auth/register'], {
        queryParams: { returnUrl: target },
      });
    } else {
      this.router.navigate(['/auth/register']);
    }
  }

  /**
   * Redireciona para o login guardando a URL de destino.
   */
  redirectToLogin(returnUrl?: string): void {
    const target = returnUrl || this.getReturnUrl();
    if (target && this.isValidReturnUrl(target)) {
      this.setReturnUrl(target);
      this.router.navigate(['/auth/login'], {
        queryParams: { returnUrl: target },
      });
    } else {
      this.router.navigate(['/auth/login']);
    }
  }

  /**
   * Navega para a URL guardada após autenticação bem-sucedida, ou para o destino padrão.
   */
  navigateAfterAuth(route?: ActivatedRoute, defaultUrl: string = '/'): void {
    const target = this.getReturnUrl(route) || defaultUrl;
    this.clearReturnUrl();
    this.router.navigateByUrl(target);
  }
}
